import _ from "lodash";
import {
  MAX_ORDERS,
  NOMENCLATURE_ORDER_CLARIFICATIONS_LABEL,
} from "./constants";
import {
  OrderType,
  ProcedureType,
} from "../../../utils/homologar/search/utils/constants";

export const isEmptyOrder = (order) =>
  _.isEmpty(order.items) && _.isEmpty(removeEmptyString(order.clarifications));

/**
 * Returns the max items for a card based on its procedureType. Default is 1 for
 * imaging (today's behavior: cada imagen sale en una card => pdf distinto) — but
 * some health centers (ej. Tandil, vía `orders.homologar.imagingMaxItemsPerCard`
 * en REACT_APP_HEALTH_CENTERS_RULES) quieren órdenes de imágenes unificadas
 * (varios estudios en una sola card/pdf), igual que ya soporta el flujo v1 vía
 * `typesConfig[].maxItemsPerOrder`. `null` = sin límite, igual que laboratorio.
 */
export const getCardMaxItems = (procedureType, imagingMaxItemsPerCard = 1) =>
  procedureType === ProcedureType.IMAGING ? imagingMaxItemsPerCard : null;

export const reachedOrderLimit = (order, maxItemsPerOrder, imagingMaxItemsPerCard) => {
  const effectiveMax = maxItemsPerOrder ?? getCardMaxItems(order.procedureType, imagingMaxItemsPerCard);
  return (
    (effectiveMax !== null && order.items.length >= effectiveMax) ||
    (removeEmptyString(order.clarifications) !== "" && order.items.length === 0) ||
    !!order.template
  );
};

export const removeEmptyString = (str) => str.replace(/\n/g, "");

export const accumulateClarifications = (clarifications) => {
  return clarifications
    .join("\n")
    .split("\n")
    .map((str) => removeEmptyString(str))
    .filter((str) => str !== "")
    .join("\n");
};

export const isThereEmptyOrder = (orders) => {
  return !_.isEmpty(orders) && orders.some((o) => isEmptyOrder(o));
};

export const isThereAtLeastOneFilledFormOrder = (form) => {
  return form.medicines.some((medicine) => (medicine.text ?? "").trim() !== "");
};

/**
 * Identity key for an order item: procedureId when present (repeated nomenclature
 * selections), falling back to name for free-text/template items that don't carry one.
 */
export const itemKey = (item) => item.procedureId ?? item.name;

/** Dedupe within a single card's items — see `itemKey`. */
const dedupeItems = (items) => _.uniqBy(items, itemKey);

/** v1 logic: finds an empty or not-full slot and fills it, or appends. */
const manageSetNewItemV1 = (order, setOrders, maxItemsPerOrder) => {
  setOrders((prev) => {
    const hasTemplate = !!order.template;
    const newOrders = [...prev];
    const emptyOrderIndex = newOrders.findIndex((o) => isEmptyOrder(o));
    const notFullOrderIndex = newOrders.findLastIndex(
      (o) => !reachedOrderLimit(o, maxItemsPerOrder) && !hasTemplate
    );
    const currentIndex =
      emptyOrderIndex !== -1 ? emptyOrderIndex : notFullOrderIndex;

    if (currentIndex !== -1) {
      const current = newOrders[currentIndex];
      newOrders[currentIndex] = {
        ...current,
        items: [...current.items, ...order.items],
        clarifications: accumulateClarifications([
          current.clarifications,
          order.clarifications,
        ]),
        template: order.template,
      };
    } else if (newOrders.length < MAX_ORDERS) {
      newOrders.push(order);
    }

    return newOrders.map((o) => ({ ...o, items: dedupeItems(o.items) }));
  });
};

/**
 * v2 logic:
 * - Each card has a procedureType (null = untyped/empty).
 * - Imaging: `imagingMaxItemsPerCard` items per card (default 1 — cada imagen es
 *   una card/pdf distinto). Health centers que quieran órdenes de imágenes
 *   unificadas (ej. Tandil, ver `getCardMaxItems`) configuran un valor mayor o
 *   `null` para accumular igual que laboratorio.
 * - Laboratory: N items per card — accumulates in the last lab card if available.
 * - Type mismatch, or last card already at its max: always opens a new card.
 * - Untyped card: takes the type of the first item inserted.
 */
/**
 * Pure card placement (unified "Prácticas" UI): returns the cards after adding `order`, and whether it fit.
 * Merging into the last card is always allowed; a new card is only opened below MAX_ORDERS.
 */
export const placeItemsInCards = (prevOrders, order, imagingMaxItemsPerCard) => {
  const incomingType = order.procedureType ?? null;
  const newOrders = [...prevOrders];
  const dedupeAll = (orders) => orders.map((o) => ({ ...o, items: dedupeItems(o.items) }));

  if (newOrders.length === 0) {
    return { orders: dedupeAll([{ ...order }]), placed: true };
  }

  const lastOrder = newOrders[newOrders.length - 1];
  const lastIsEmpty = isEmptyOrder(lastOrder);
  const lastType = lastOrder.procedureType;
  // Laboratorio siempre fusiona (sin límite). Imaging fusiona solo si hay lugar
  // según el límite configurado. Cualquier otro tipo (null/manual) nunca
  // fusiona entre cards no vacías — mismo comportamiento que antes de sumar el
  // límite configurable, para no fusionar por accidente cards de "Otros".
  const lastMaxImagingItems = getCardMaxItems(ProcedureType.IMAGING, imagingMaxItemsPerCard);
  // `items.length > 0` es defensivo: hoy sacar el último ítem de una card la
  // splicea entera (ver onOrderItemRemove en Orders.jsx), así que una card
  // "imaging" con 0 ítems no debería poder existir — pero si algo cambia eso
  // en el futuro, no queremos que una card typeada-pero-vacía empiece a
  // aceptar merges por accidente (mismo comportamiento que hoy: SIEMPRE abre
  // card nueva salvo que ya tenga al menos un ítem con lugar disponible).
  const lastImagingHasRoom =
    lastType === ProcedureType.IMAGING &&
    lastOrder.items.length > 0 &&
    (lastMaxImagingItems === null || lastOrder.items.length < lastMaxImagingItems);

  const canMerge =
    lastIsEmpty ||
    (lastType === incomingType &&
      (lastType === ProcedureType.LABORATORY || lastImagingHasRoom) &&
      !lastOrder.template &&
      !order.template);

  if (canMerge) {
    newOrders[newOrders.length - 1] = {
      ...lastOrder,
      items: [...lastOrder.items, ...order.items],
      clarifications: accumulateClarifications([
        lastOrder.clarifications,
        order.clarifications,
      ]),
      template: order.template ?? lastOrder.template,
      procedureType: incomingType ?? lastType,
    };
    return { orders: dedupeAll(newOrders), placed: true };
  }

  if (newOrders.length >= MAX_ORDERS) {
    return { orders: dedupeAll(newOrders), placed: false };
  }

  newOrders.push({ ...order });
  return { orders: dedupeAll(newOrders), placed: true };
};

/**
 * Plans adding several groups of items at once (a template, or a single search pick) with the
 * same placement as one-by-one additions: each group is split into cards of
 * `getCardMaxItems(procedureType, imagingMaxItemsPerCard)` items. All or nothing: `fits` is false
 * if any card would go beyond MAX_ORDERS.
 */
export const planCardsPlacement = (orders, groups, imagingMaxItemsPerCard) =>
  groups.reduce(
    (plan, { procedureType, items, template = null }) => {
      const maxPerCard = getCardMaxItems(procedureType, imagingMaxItemsPerCard);
      const chunks = maxPerCard ? _.chunk(items, maxPerCard) : [items];
      return chunks.reduce((current, chunk) => {
        const { orders: nextOrders, placed } = placeItemsInCards(
          current.orders,
          { items: chunk, clarifications: "", template, procedureType },
          imagingMaxItemsPerCard
        );
        return { orders: nextOrders, fits: current.fits && placed };
      }, plan);
    },
    { orders, fits: true }
  );

const manageSetNewItemV2 = (order, setOrders, imagingMaxItemsPerCard) => {
  setOrders((prev) => placeItemsInCards(prev, order, imagingMaxItemsPerCard).orders);
};

export const manageSetNewItem = (
  order,
  setOrders,
  maxItemsPerOrder,
  isV2 = false,
  imagingMaxItemsPerCard
) => {
  if (isV2) {
    manageSetNewItemV2(order, setOrders, imagingMaxItemsPerCard);
  } else {
    manageSetNewItemV1(order, setOrders, maxItemsPerOrder);
  }
};

/**
 * Splits `itemsArray` into the cards it'll take (chunks of `effectiveMaxPerCard`,
 * or the whole batch as one chunk when there's no per-card limit or it already
 * fits), or reports it doesn't fit under `MAX_ORDERS`.
 *
 * `reservedCards` accounts for cards a sibling procedureType group already
 * claimed earlier in the same template application (see groupItemsByProcedureType /
 * addOrderItemsByTemplate) — `nonEmptyOrdersCount` alone reflects the order
 * *before* this application started, so without `reservedCards` a later group
 * could think there's room a previous group in the same batch already used.
 */
export const planCardChunks = (itemsArray, effectiveMaxPerCard, nonEmptyOrdersCount, reservedCards = 0) => {
  if (!effectiveMaxPerCard || itemsArray.length <= effectiveMaxPerCard) {
    return { chunks: [itemsArray] };
  }

  const chunks = _.chunk(itemsArray, effectiveMaxPerCard);
  const ordersLeft = MAX_ORDERS - nonEmptyOrdersCount - reservedCards;
  if (chunks.length > ordersLeft) {
    return { chunks: null };
  }

  return { chunks };
};

/**
 * Groups a template's items by their own procedureType (hydrated per item), falling
 * back to `fallbackProcedureType` for items without one (e.g. legacy free-text items).
 * Lets a mixed template (DEV-1687, v3) be split into separate cards per type when
 * applied — laboratory merges into one, imaging respects imagingMaxItemsPerCard —
 * the same grouping a manually built order gets.
 */
export const groupItemsByProcedureType = (items, fallbackProcedureType = null) => {
  const resolveType = (item) => item.procedureType ?? fallbackProcedureType;
  const procedureTypes = _.uniq(items.map(resolveType));
  return procedureTypes.map((procedureType) => ({
    procedureType,
    items: items.filter((item) => resolveType(item) === procedureType),
  }));
};

/** Structured order item payload for POST /orders: {procedureId} for nomenclated items, {text} otherwise. */
const toOrderItemPayload = (item) =>
  item.procedureId != null ? { procedureId: item.procedureId } : { text: item.name };

export const mapOrderToForm = (order, fallbackOrderType = null) => {
  // Trimmed to match the API's own trimming of free-typed item text (OrderItemDto):
  // sending the untrimmed value here would both let whitespace-only input slip past
  // the "is this order empty" checks and diverge from the `medicine` the API persists.
  const clarifications = (order.clarifications ?? "").trim();
  const itemsText = order.items.map((item) => item.name).join("\n");
  const clarificationsText =
    clarifications === ""
      ? ""
      : `\n\n${NOMENCLATURE_ORDER_CLARIFICATIONS_LABEL}:\n${clarifications}`;

  const text =
    removeEmptyString(itemsText) === ""
      ? clarifications
      : `${itemsText}${clarificationsText}`;

  const hasItems = order.items.length > 0;
  const items = hasItems
    ? order.items.map(toOrderItemPayload)
    : clarifications !== ""
    ? [{ text: clarifications }]
    : undefined;

  // PRACTICES is a UI tab, not a value of prescription_ordertype_enum.
  const rawOrderType = order.procedureType ?? fallbackOrderType;
  const orderType = rawOrderType === OrderType.PRACTICES ? OrderType.MANUAL : rawOrderType;

  return {
    text,
    template: order.template,
    orderType,
    items,
    notes: hasItems ? clarifications : undefined,
  };
};
